import React, { useState } from 'react';
import { Briefcase, PlusCircle, X } from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';
import toast from 'react-hot-toast';

const CreateFirstPortfolio = () => {
  const { createPortfolio } = usePortfolio();
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter a portfolio name');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Creating portfolio...');

    try {
      await createPortfolio(name.trim(), description.trim());
      toast.success('Portfolio created successfully!', { id: loadingToast });
      setShowModal(false);
      setName('');
      setDescription('');
    } catch (error: any) {
      toast.error(`Failed to create portfolio: ${error.message}`, { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center max-w-2xl mx-auto">
        <div className="mb-6">
          <Briefcase size={64} className="mx-auto text-blue-600 mb-4" />
        </div>
        
        <h2 className="text-2xl font-bold text-slate-900 mb-3">Welcome to AiLabQuant</h2>
        
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          To get started, create your first portfolio. Portfolios help you organize your investments,
          whether you're paper trading a new strategy or tracking a real account.
        </p>
        
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-sm"
        >
          <PlusCircle size={20} />
          Create Your First Portfolio
        </button>
      </div>

      {/* Create Portfolio Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => !isSubmitting && setShowModal(false)}
        >
          <div 
            className="bg-white w-full max-w-md rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="text-lg font-bold text-slate-900">Create Portfolio</h3>
              <button 
                onClick={() => !isSubmitting && setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                disabled={isSubmitting}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Portfolio Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Growth Portfolio"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this portfolio for?"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Portfolio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateFirstPortfolio;

